import React, {createContext, useContext, useState} from 'react'
import {SiteModel} from '../../../central/model'
import {selectMapperByKeys} from '../../mappers/util'
import {ScheduleAssignmentFilter, ScheduleAssignModel} from '../../model/assigment.model'
import {getListScheduling, getListSchedulingFilter} from '../../services/assigment.services'
import {useCentralServices} from '../central/useCentralServices'
import {CREATION_CLEANING_ACTIVITIES_SERVICES} from '../../services/creation-cleaning-activities.service'
import {MULTI_USER_CREW_SERVICES} from '../../services/multi-user-crew.service'
import {
  ModulesByServiceSchedulingDto,
  ModulesByServiceSchedulingResponse,
  RoomsByServiceSchedulingDto,
  RoomsByServiceSchedulingResponse,
} from '../../model/multiUserCrew.model'

/* =========================
   TYPES
========================= */

type MultiUserContextType = {
  loadingCampaments: boolean
  loadingSchedulesService: boolean
  loadingListScheduling: boolean
  loadingCleaningActivities: boolean
  loadingModules: boolean
  loadingRooms: boolean
  campaments: any[]
  schedules: any[]
  scheduleAssign: ScheduleAssignModel[]
  cleaningActivities: any[]
  modules: any[]
  rooms: any[]
  getCampaments: (
    filters: SiteModel,
    page: number,
    options?: {all: boolean; value: any; label: string},
  ) => Promise<void>
  getSchedulesService: (filters: ScheduleAssignmentFilter) => Promise<void>
  getListScheduling: (
    filters: ScheduleAssignmentFilter,
    handlePagination: (filters: ScheduleAssignmentFilter, total: number) => void,
  ) => Promise<void>
  getActivitiesByCampamentoList: (filters: any) => Promise<void>
  getModules: (filters: ModulesByServiceSchedulingDto) => Promise<void>
  getRooms: (filters: RoomsByServiceSchedulingDto) => Promise<void>
  clearCampaments: () => void
  clearSchedules: () => void
  clearScheduleAssign: () => void
  clearCleaningActivities: () => void
  clearModules: () => void
  clearRooms: () => void
}

/* =========================
   LOGICA INTERNA (PRIMERO)
========================= */

const useMultiUserProvider = (): MultiUserContextType => {
  const [loadingCampaments, setLoadingCampaments] = useState(false)
  const [loadingSchedulesService, setLoadingSchedulesService] = useState(false)
  const [loadingListScheduling, setLoadingListScheduling] = useState(false)
  const [loadingCleaningActivities, setLoadingCleaningActivities] = useState(false)
  const [loadingModules, setLoadingModules] = useState(false)
  const [loadingRooms, setLoadingRooms] = useState(false)
  const [campaments, setCampaments] = React.useState<{value: string; label: string}[]>([])
  const [schedules, setSchedules] = React.useState<{value: string; label: string}[]>([])
  const [scheduleAssign, setScheduleAssign] = React.useState<ScheduleAssignModel[]>([])
  const [cleaningActivities, setCleaningActivities] = useState<{value: string; label: string}[]>([])
  const [modules, setModules] = useState<{value: string; label: string}[]>([])
  const [rooms, setRooms] = useState<{value: string; label: string}[]>([])
  const {getCamps} = useCentralServices()

  const getCampaments = async (
    filters: SiteModel,
    page: number,
    options: {all: boolean; value: any; label: string} = {all: false, value: null, label: 'TODOS'},
  ) => {
    try {
      setLoadingCampaments(true)
      const campaments = await getCamps(filters, page)
      const mappedCampaments = selectMapperByKeys(campaments, 'id', 'nombre')
      let filteredData = mappedCampaments.filter((item) => item.value !== '4EsB2NAAsDA=')
      if (options.all && filteredData.length > 0) {
        filteredData = [{value: options.value, label: options.label}, ...filteredData]
        setCampaments(filteredData)
      } else {
        setCampaments(filteredData)
      }
    } catch (error) {
      console.error('Error fetching campaments:', error)
    } finally {
      setLoadingCampaments(false)
    }
  }

  const getSchedulesService = async (filters: ScheduleAssignmentFilter) => {
    try {
      setLoadingSchedulesService(true)
      const response = await getListSchedulingFilter(filters)
      const programmingServices = response.data.data
      const mappedProgrammingServices = selectMapperByKeys(
        programmingServices,
        'Id',
        'Nomenclatura',
      )
      setSchedules(mappedProgrammingServices)
    } catch (error) {
      console.error('Error fetching programming services:', error)
      setSchedules([])
    } finally {
      setLoadingSchedulesService(false)
    }
  }

  const getListSchedulingFn = async (
    filters: ScheduleAssignmentFilter,
    handlePagination: (filters: ScheduleAssignmentFilter, total: number) => void,
  ) => {
    try {
      setLoadingListScheduling(true)
      const response = await getListScheduling(filters)
      setScheduleAssign(response.data.data)
      handlePagination(filters, response.data.total)
    } catch (error) {
      console.error('Error fetching scheduling list:', error)
      setScheduleAssign([])
      handlePagination(filters, 0)
    } finally {
      setLoadingListScheduling(false)
    }
  }

  const getActivitiesByCampamentoList = async (filters: any) => {
    try {
      const {data: response} = await CREATION_CLEANING_ACTIVITIES_SERVICES.getByServiceSchedulingId(
        {
          pageNumber: 1,
          size: 100,
          CampamentoId: filters.CampamentoId || '',
          ServiceSchedulingId: filters.ServiceSchedulingId || '',
          Disponibles: false,
        },
      )
      if (!response.error) {
        const data = response.data
        const mappedData = data.map((item: any) => ({
          value: item.Id,
          label: `${item.Actividad} - ${item.TipoAccion} - ${item.Modulo} - ${item.Habitacion}`,
        }))
        setCleaningActivities(mappedData)
      }
    } catch (error) {
      console.error('Error fetching cleaning activities:', error)
    } finally {
      setLoadingCleaningActivities(false)
    }
  }

  const getModules = async (filters: ModulesByServiceSchedulingDto) => {
    try {
      setLoadingModules(true)
      const response = await MULTI_USER_CREW_SERVICES.getModules(filters)
      if (!response.data.error) {
        const data = response.data.data
        const mappedData = data.map((item: ModulesByServiceSchedulingResponse) => ({
          value: item.Id,
          label: item.Nombre,
        }))
        if (mappedData.length === 0) {
          setModules(mappedData)
        } else {
          const allItem = {value: '4EsB2NAAsDA=', label: 'TODOS'}
          setModules([allItem, ...mappedData])
        }
      }
    } catch (error) {
      console.error('Error fetching modules:', error)
    } finally {
      setLoadingModules(false)
    }
  }

  const getRooms = async (filters: RoomsByServiceSchedulingDto) => {
    try {
      setLoadingRooms(true)
      const response = await MULTI_USER_CREW_SERVICES.getRooms(filters)
      if (!response.data.error) {
        const data = response.data.data
        const mappedData = data.map((item: RoomsByServiceSchedulingResponse) => ({
          value: item.Id,
          label: item.Nombre,
        }))
        if (mappedData.length === 0) {
          setRooms(mappedData)
        } else {
          const allItem = {value: '4EsB2NAAsDA=', label: 'TODOS'}
          setRooms([allItem, ...mappedData])
        }
      }
    } catch (error) {
      console.error('Error fetching rooms:', error)
    } finally {
      setLoadingRooms(false)
    }
  }

  const clearCampaments = () => setCampaments([])
  const clearSchedules = () => setSchedules([])
  const clearScheduleAssign = () => setScheduleAssign([])
  const clearCleaningActivities = () => setCleaningActivities([])
  const clearModules = () => setModules([])
  const clearRooms = () => setRooms([])

  return {
    loadingCampaments,
    loadingSchedulesService,
    loadingListScheduling,
    loadingCleaningActivities,
    loadingModules,
    loadingRooms,
    campaments,
    schedules,
    scheduleAssign,
    cleaningActivities,
    modules,
    rooms,
    getCampaments,
    getSchedulesService,
    getListScheduling: getListSchedulingFn,
    getActivitiesByCampamentoList,
    getModules,
    getRooms,
    clearCampaments,
    clearSchedules,
    clearScheduleAssign,
    clearCleaningActivities,
    clearModules,
    clearRooms,
  }
}

/* =========================
   CONTEXT
========================= */

const MultiUserContext = createContext<MultiUserContextType | null>(null)

/* =========================
   PROVIDER
========================= */

export const MultiUserProvider = ({children}: {children: React.ReactNode}) => {
  const value = useMultiUserProvider()
  return <MultiUserContext.Provider value={value}>{children}</MultiUserContext.Provider>
}

/* =========================
   HOOK PUBLICO
========================= */

export const useMultiUser = () => {
  const context = useContext(MultiUserContext)
  if (!context) {
    throw new Error('useMultiUser must be used within a MultiUserProvider')
  }
  return context
}
